import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/ui/container';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Coins as CoinsIcon, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { PAYPAL_CLIENT_ID } from '@/lib/paypal';
import { sendCreditsConfirmationEmail } from '@/lib/actions/emailActions';

interface CoinPackageProps { 
  title: string;
  price: number;
  coins: number;
  description: string;
  isPopular?: boolean;
  onSelect: () => void;
}

const CoinPackage: React.FC<CoinPackageProps> = ({ 
  title, 
  price, 
  coins, 
  description, 
  isPopular = false,
  onSelect 
}) => {
  return (
    <Card className={`${isPopular ? 'border-brand-500 shadow-lg' : ''} flex flex-col`}>
      {isPopular && (
        <div className="bg-brand-500 text-white text-center py-1 rounded-t-lg text-sm font-medium">
          Më i Preferuar
        </div>
      )}
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <div className="text-3xl font-bold mb-4">€{price.toFixed(2)}</div>
        <div className="flex items-center gap-2 text-lg mb-6">
          <CoinsIcon className="text-yellow-500" />
          <span><strong>{coins}</strong> kreditë</span>
        </div>
        
        <ul className="space-y-2">
          <li className="flex items-start">
            <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-sm">1 kredi = 1 shpallje</span>
          </li>
          <li className="flex items-start">
            <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-sm">Përdorimi i menjëhershëm</span>
          </li>
          <li className="flex items-start">
            <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-sm">Pa skadencë</span>
          </li>
        </ul>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={onSelect} 
          className={`w-full ${isPopular ? 'bg-brand-500 hover:bg-brand-600' : ''}`}
        >
          Blej Tani
        </Button>
      </CardFooter>
    </Card>
  );
};


interface UserCreditsRpcData {
  free_listings_remaining: number;
  purchased_listings_credits: number;
  default_free_listings: number;
}

const CoinsPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<{coins: number, price: number} | null>(null);
  const [showPayPal, setShowPayPal] = useState(false);

  const { data: userCredits } = useQuery({
    queryKey: ['userCreditsRpcData', profile?.id],
    queryFn: async (): Promise<UserCreditsRpcData | null> => {
      if (!profile?.id) return null;
      
      try {
        const { data: rpcResponse, error: rpcError } = await supabase.rpc(
          'get_user_credits', 
          { p_user_id: profile.id }
        );
        
        if (rpcError) throw rpcError;
        
        const rpcData = rpcResponse?.[0];
        
        if (!rpcData) {
          console.warn("CoinsPage: RPC get_user_credits returned no data for user", profile.id);
          return {
            free_listings_remaining: 0,
            purchased_listings_credits: 0,
            default_free_listings: 0,
          };
        }
        
        return {
          free_listings_remaining: rpcData.free_listings_remaining ?? 0,
          purchased_listings_credits: rpcData.purchased_listings_credits ?? 0,
          default_free_listings: rpcData.default_free_listings ?? 0,
        };

      } catch (error) {
        console.error('Error fetching user credits in CoinsPage:', error);
        return {
          free_listings_remaining: 0,
          purchased_listings_credits: 0,
          default_free_listings: 0,
        };
      }
    },
    enabled: !!profile?.id && isAuthenticated,
  });

  const handleSelectPackage = (coins: number, price: number) => {
    if (!profile?.id) {
      toast({
        title: "Ju lutem identifikohuni",
        description: "Duhet të keni një llogari për të blerë kreditë",
        variant: "destructive"
      });
      navigate('/auth');
      return;
    }

    setSelectedPackage({ coins, price });
    setShowPayPal(true);
  };

  const handleBuyCoinsSuccess = async (amount: number) => {
    if (!profile?.id) return;
    
    setIsProcessing(true);

    try {      
      const { data: existingCredits, error: fetchError } = await supabase
        .from('user_credits')
        .select('purchased_listings_credits, free_listings_used, free_stocks_used')
        .eq('user_id', profile.id)
        .maybeSingle();
      
      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;
      
      const currentPurchasedCredits = existingCredits?.purchased_listings_credits || 0;
      const currentFreeListingsUsed = existingCredits?.free_listings_used || 0;
      const currentFreeStocksUsed = existingCredits?.free_stocks_used || 0;
      const newCreditTotal = currentPurchasedCredits + amount;
      
      const { error: upsertError } = await supabase
        .from('user_credits')
        .upsert({ 
          user_id: profile.id, 
          purchased_listings_credits: newCreditTotal,
          free_listings_used: currentFreeListingsUsed, 
          free_stocks_used: currentFreeStocksUsed,
          updated_at: new Date().toISOString(),
        }, { 
          onConflict: 'user_id' 
        });
      
      if (upsertError) throw upsertError;
      
      const price = selectedPackage?.price || 0;
      
      try {
        const { data: userData } = await supabase
          .from('profiles')
          .select('email, username')
          .eq('id', profile.id)
          .single();
          
        if (userData?.email) {
          await sendCreditsConfirmationEmail(
            userData.email,
            userData.username || 'User',
            amount,
            price,
            'EUR'
          );
        }
      } catch (emailError) {
        console.error('Error sending credits confirmation email:', emailError);
      }
      
      queryClient.invalidateQueries({ queryKey: ['userCreditsRpcData', profile.id] });
      
      toast({
        title: "Sukses",
        description: `${amount} kredite u shtuan në llogarinë tuaj.`,
        variant: "default"
      });

      setShowPayPal(false);
      setSelectedPackage(null);
    } catch (error) {
      console.error('Error purchasing credits:', error);
      toast({
        title: "Blerja dështoi",
        description: "Ndodhi një gabim gjatë përpunimit të pagesës. Ju lutemi provoni përsëri.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const totalCredits = (userCredits?.free_listings_remaining || 0) + 
                      (userCredits?.purchased_listings_credits || 0);
  const defaultFree = userCredits?.default_free_listings || 0;


  return (
    <PayPalScriptProvider options={{ 
      clientId: PAYPAL_CLIENT_ID,
      currency: 'EUR',
      intent: 'capture'
    }}>
      <div className="flex flex-col min-h-screen">
        <Navbar />
        
        <main className="flex-grow py-16">
          <Container>
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-3xl font-bold mb-4">Kreditë e Shpalljeve</h1>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Blini kreditë për të publikuar më shumë shpallje në platformën tonë.
                  Çdo kredi ju lejon të krijoni një shpallje të re.
                </p>
                
                {isAuthenticated && userCredits && (
                  <div className="mt-6 inline-block bg-gray-50 dark:bg-gray-800 px-6 py-3 rounded-full">
                    <span className="font-medium dark:text-gray-300">Kreditë tuaja aktuale:</span>{' '}
                    <span className="text-brand-500 font-bold">{totalCredits}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                      ({userCredits.free_listings_remaining} falas nga {defaultFree} mujore, {userCredits.purchased_listings_credits} të blera)
                    </span>
                  </div>
                )}
              </div>
              
              {showPayPal && selectedPackage ? (
                <div className="max-w-md mx-auto mb-10 p-6 border rounded-lg shadow-md dark:bg-gray-800 dark:border-gray-700">
                  <h2 className="text-xl font-bold mb-4 dark:text-white">Paguaj për {selectedPackage.coins} kredite</h2>
                  <p className="mb-4 dark:text-gray-300">Çmimi total: €{selectedPackage.price.toFixed(2)}</p>
                  
                  <PayPalButtons 
                    style={{ layout: 'vertical' }}
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        intent: "CAPTURE",
                        purchase_units: [
                          {
                            amount: {
                              currency_code: 'EUR',
                              value: selectedPackage.price.toString()
                            },
                            description: `${selectedPackage.coins} kredite për shpallje`
                          }
                        ]
                      });
                    }}
                    onApprove={async (data, actions) => {
                        if (actions.order) {
                           await actions.order.capture();
                           handleBuyCoinsSuccess(selectedPackage.coins);
                        } else {
                            toast({
                                title: "Gabim në pagesë",
                                description: "Detajet e porosisë nuk mundën të merreshin.",
                                variant: "destructive"
                            });
                        }
                    }}
                    onCancel={() => {
                      setShowPayPal(false);
                      toast({
                        description: "Blerja u anulua.",
                        variant: "default"
                      });
                    }}
                    onError={(err) => {
                      console.error("PayPal Error:", err);
                      toast({
                        title: "Gabim në pagesë",
                        description: "Ka ndodhur një gabim gjatë procesimit të pagesës.",
                        variant: "destructive"
                      });
                    }}
                  />
                  
                  <Button 
                    variant="outline" 
                    className="w-full mt-4" 
                    onClick={() => setShowPayPal(false)}
                  >
                    Kthehu
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <CoinPackage
                    title="Paketë Fillestare"
                    price={4.90}
                    coins={10}
                    description="Paketë ekonomike për fillimin"
                    onSelect={() => handleSelectPackage(10, 4.90)}
                  />
                  
                  <CoinPackage
                    title="Paketë Standarde"
                    price={9.90}
                    coins={20}
                    description="Opsioni më i mirë për shumicën e përdoruesve"
                    isPopular={true}
                    onSelect={() => handleSelectPackage(20, 9.90)}
                  />
                  
                  <CoinPackage
                    title="Paketë Pro"
                    price={19.90}
                    coins={50}
                    description="Ideal për shitësit e rregullt"
                    onSelect={() => handleSelectPackage(50, 19.90)}
                  />
                </div>
              )}
              
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mt-12">
                <h3 className="text-lg font-medium mb-4 dark:text-white">Informacione të Rëndësishme për Kreditë</h3>
                <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Çdo përdorues i ri merr {defaultFree > 0 ? defaultFree : 'disa'} shpallje falas mujore pas regjistrimit</span>
                  </li>
                   <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Kreditë shtesë mund të blihen në çdo kohë dhe nuk skadojnë kurrë</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Çdo kredi ju lejon të krijoni 1 shpallje të re në platformë</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Kreditë përdoren automatikisht kur krijoni shpallje të reja</span>
                  </li>
                </ul>
                
                {!isAuthenticated && (
                  <div className="mt-6">
                    <Button onClick={() => navigate('/auth')}>
                      Identifikohu për të Blerë Kreditë
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Container>
        </main>
        
        <Footer />
      </div>
    </PayPalScriptProvider>
  );
};

export default CoinsPage;
